// External dependencies.
import { LitElement, html, css } from "lit";
import $ from "jquery";

// Internal dependencies.
import { sharedStyles } from "./styles/shared-styles";

/**
 * AppProductPrice component class.
 *
 * @since 0.14.1
 * @class AppProductPrice
 * @extends {LitElement}
 */
class AppProductPrice extends LitElement {
    /**
     * Creates an instance of AppProductPrice.
     * 
     * @since 0.14.1
     * @memberof AppProductPrice
     */
    constructor() {
        super();
        this.price = 0;
        this.regularPrice = 0;
        this.quantity = 0;
        this.showRegularPrice = false;
        this.isVariableProduct = false;
        this.variationForm = null;
        this.rangePrice = null;

        this.calcPrice = this.calcPrice.bind( this );
        this.setVariationPriceVisibility = this.setVariationPriceVisibility.bind( this );
    }

    /**
     * Inits the component's properties.
     *
     * @since 0.14.1
     * @readonly
     * @static
     * @memberof AppProductPrice
     */
    static get properties() {
        return {
            currencySymbol: { type: String },
            price: { type: Number },
            regularPrice: { type: Number },
            quantity: { type: Number },
            showRegularPrice: { type: Boolean },
        };
    }

    /**
     * Inits the component's CSS styles.
     *
     * @since 0.14.1
     * @readonly
     * @static
     * @memberof AppProductPrice
     */
    static get styles() {
        return [ sharedStyles, css`
            .product-price {
                align-items: center;
                font-size: 1.75rem;
                font-family: var(--font-lilita);
            }

            .product-price__regular {
                margin-right: .25rem;
                opacity: .8;
                font-size: 1.5rem;
            }

            :host(.ts-sticky-cart__variation-price) .product-price {
                font-size: 1.25rem;
            }

            :host(.ts-sticky-cart__variation-price) .product-price__regular {
                font-size: 1rem;
            }
        ` ];
    }

    /**
     * Runs when the component is appended to the document.
     *
     * @since 0.14.1
     * @listens event:productQuantityChanged
     * @listens event:update_variation_values
     * @listens event:found_variation
     * @memberof AppProductPrice
     */
    connectedCallback() {
        super.connectedCallback();
        const { price, regular_price, type } = window.app.product;
        this.isVariableProduct = 'variable' === type;

        if ( this.isVariableProduct ) {
            this.variationForm = document.querySelector( '.single-product .summary form.variations_form' );
            this.rangePrice = document.querySelector( '.single-product .summary .ts-variable-product-price p.price' );
            this.setVariationPriceVisibility( false );
            this.variationForm && $( this.variationForm ).on(
                'show_variation.appProductPrice',
                ( event, variation, purchasable ) => {
                    this.setVariationPriceVisibility( !! purchasable );
                }
            );
            this.variationForm && $( this.variationForm ).on(
                'hide_variation.appProductPrice reset_data.appProductPrice',
                () => this.setVariationPriceVisibility( false )
            );
        } else {
            this.price = parseFloat( price );
            this.regularPrice = parseFloat( regular_price );
            this.showRegularPrice = ( this.price < this.regularPrice );
        }

        document.addEventListener( 'productQuantityChanged', e => this.calcPrice( e.detail.qty, e.detail.mix_and_match_data ) );

        $( document ).on( 'update_variation_values', () => {
            this.price = 0;
            this.regularPrice = 0;
        } );

        $( document ).on( 'found_variation', () => {
            const qty = parseInt( document.querySelector( 'input[name="quantity"]' ).value );
            this.calcPrice( qty, null );
        } );
    }

    /**
     * Shows the selected variation price only when WooCommerce marks it purchasable.
     *
     * @param {boolean} visible Whether the variation price should be displayed.
     */
    setVariationPriceVisibility( visible ) {
        if ( ! this.isVariableProduct ) {
            return;
        }

        this.hidden = ! visible;
        if ( this.rangePrice ) {
            this.rangePrice.hidden = visible;
        }
    }

    /**
     * Calculates the product's price.
     *
     * @since 0.14.1
     * @param Number quantity
     * @memberof AppProductPrice
     */
    calcPrice( quantity, mix_and_match_data ) {
        const qty = parseInt( quantity );
        const { price, regular_price, type, variations, priced_per_product } = window.app.product;

        if ( isNaN( qty ) || ( 'variable' === type && 0 >= variations.length ) ) {
            this.price = 0;
            this.regularPrice = 0;
            return;
        }

        if ( 'variable' === type ) {
            const variation_id = document.querySelector( 'input[name="variation_id"]' ).value;
            const i = variations.findIndex( variation => variation.id == variation_id );

            if ( -1 === i ) {
                this.price = 0;
                this.regularPrice = 0;
                return;
            }

            this.price = parseFloat( variations[ i ].price * qty );
            this.regularPrice = parseFloat( variations[ i ].regular_price * qty );
        } else if ( 'mix-and-match' === type && priced_per_product ) {
            if ( null === mix_and_match_data || 0 >= mix_and_match_data.length ) {
                this.price = 0;
                this.regularPrice = 0;
                return;
            }

            let mnm_price = 0;
            let mnm_regular_price = 0;

            mix_and_match_data.forEach( item => {
                mnm_price += item.price * item.qty;
                mnm_regular_price += item.regular_price * item.qty;
            } );

            this.price = ( 0 >= parseFloat( price ) ) ? parseFloat( qty * mnm_price ) : parseFloat( ( price * qty ) + mnm_price );
            this.regularPrice = ( 0 >= parseFloat( regular_price ) ) ? parseFloat( qty * mnm_regular_price ) : parseFloat( ( regular_price * qty ) + mnm_regular_price );
        } else {
            this.price = parseFloat( price * qty )
            this.regularPrice = parseFloat( regular_price * qty );
        }

        this.showRegularPrice = ( this.price < this.regularPrice );
    }

    /**
     * Returns the regular price HTML template.
     *
     * @since 0.14.1
     * @readonly
     * @memberof AppProductPrice
     */
    get regularPriceTemplate() {
        return html`
            ${this.showRegularPrice ? html`<p class="product-price__regular"><del>${this.currencySymbol}${this.regularPrice.toFixed( 2 )}</del></p>` : null}
        `;
    }

    /**
     * Renders the component.
     *
     * @since 0.14.1
     * @returns TemplateResult
     * @memberof AppProductPrice
     */
    render() {
        return html`
            <div class="product-price${( 0 >= this.price || 0 >= this.regularPrice ) ? ' d-none' : ' d-flex'}">
                ${this.regularPriceTemplate}
                <p class="product-price__price">${this.currencySymbol}${this.price.toFixed( 2 )}</p>
            </div >
        `;
    }
}

window.customElements.define( 'app-product-price', AppProductPrice );
